// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseSuperAdminFactory instead of class inheritance

import { createBaseSuperAdmin, type IBaseSuperAdmin } from "../BaseSuperAdminFactory";
import type { ApiPayload } from "../../../types";
import type {
	AnchorTestResponse,
	SmsTestResponse,
	MailTestResponse,
	IpfsTestResponse,
	IdNotUserTestResponse,
	IdNotOfficeTestResponse,
	DatabaseSummaryResponse,
} from "./Health";
import { buildHealthTestPayload, buildPayloadOrEmpty } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

interface HealthInstance {
	triggerAnchor(body?: ApiPayload): Promise<AnchorTestResponse>;
	triggerSms(body: ApiPayload): Promise<SmsTestResponse>;
	triggerEmail(body: ApiPayload): Promise<MailTestResponse>;
	triggerIpfs(body: ApiPayload): Promise<IpfsTestResponse>;
	triggerIdNotUser(body: ApiPayload): Promise<IdNotUserTestResponse>;
	triggerIdNotOffice(body: ApiPayload): Promise<IdNotOfficeTestResponse>;
	getDatabaseSummary(): Promise<DatabaseSummaryResponse>;
}

// Module-level singleton instance
let instance: HealthInstance | null = null;
let baseSuperAdminInstance: IBaseSuperAdmin | null = null;

function buildHealthTriggers(baseSuperAdmin: IBaseSuperAdmin, baseUrl: string): Pick<
	HealthInstance,
	"triggerAnchor" | "triggerSms" | "triggerEmail" | "triggerIpfs" | "triggerIdNotUser" | "triggerIdNotOffice"
> {
	return {
		triggerAnchor: async (body?: ApiPayload): Promise<AnchorTestResponse> => {
			const url = new URL(baseUrl.concat("/tests/anchor"));
			return baseSuperAdmin.postRequest<AnchorTestResponse>(url, buildPayloadOrEmpty(body));
		},
		triggerSms: async (body: ApiPayload): Promise<SmsTestResponse> => {
			const url = new URL(baseUrl.concat("/tests/sms"));
			return baseSuperAdmin.postRequest<SmsTestResponse>(url, buildHealthTestPayload(body));
		},
		triggerEmail: async (body: ApiPayload): Promise<MailTestResponse> => {
			const url = new URL(baseUrl.concat("/tests/email"));
			return baseSuperAdmin.postRequest<MailTestResponse>(url, buildHealthTestPayload(body));
		},
		triggerIpfs: async (body: ApiPayload): Promise<IpfsTestResponse> => {
			const url = new URL(baseUrl.concat("/tests/ipfs"));
			return baseSuperAdmin.postRequest<IpfsTestResponse>(url, buildHealthTestPayload(body));
		},
		triggerIdNotUser: async (body: ApiPayload): Promise<IdNotUserTestResponse> => {
			const url = new URL(baseUrl.concat("/tests/idnot-user"));
			return baseSuperAdmin.postRequest<IdNotUserTestResponse>(url, buildHealthTestPayload(body));
		},
		triggerIdNotOffice: async (body: ApiPayload): Promise<IdNotOfficeTestResponse> => {
			const url = new URL(baseUrl.concat("/tests/idnot-office"));
			return baseSuperAdmin.postRequest<IdNotOfficeTestResponse>(url, buildHealthTestPayload(body));
		},
	};
}

function createHealth(): HealthInstance {
	if (!baseSuperAdminInstance) {
		baseSuperAdminInstance = createBaseSuperAdmin();
	}
	const baseSuperAdmin = baseSuperAdminInstance;
	const baseUrl = baseSuperAdmin.namespaceUrl.concat("/health");
	const triggers = buildHealthTriggers(baseSuperAdmin, baseUrl);
	return {
		...triggers,
		getDatabaseSummary: async (): Promise<DatabaseSummaryResponse> => {
			const url = new URL(baseUrl.concat("/database-summary"));
			return baseSuperAdmin.getRequest<DatabaseSummaryResponse>(url);
		},
	};
}

export function getHealthInstance(): HealthInstance {
	if (!instance) {
		instance = createHealth();
	}
	return instance;
}
